import { CSRF_COOKIE_NAME, SESSION_COOKIE_NAME } from "@ids/shared";
import type { PopulatedAppUser } from "@tintoa/identity-service-client";
import {
    IAddApiKeyDto,
    IAdminInvitationQuery,
    IAdminUserQuery,
    ICreateClientDto,
    ICreateInvitationAdminDto,
    ICreateMessageAdminDto,
    ICreateRole,
    ICreateUserDto,
    IUpdateClientDto,
    IUpdateUserDto
} from "@tintoa/identity-service-client";
import { InternalAdminClient } from "../api/InternalAdminClient";

export class AdminApiClient {
    private static instance: AdminApiClient;
    private client: InternalAdminClient;

    private constructor() {
        // Initialize the internal admin client with the current origin
        const baseUrl = window.location.origin;
        this.client = new InternalAdminClient(baseUrl);
    }

    public static getInstance(): AdminApiClient {
        if (!AdminApiClient.instance) {
            AdminApiClient.instance = new AdminApiClient();
        }
        return AdminApiClient.instance;
    }

    public setJwtToken(_token: string) {
        // Token is handled via cookie, no action needed
        // This method is kept for API compatibility
    }

    public clearJwtToken() {
        // Token is handled via cookie, no action needed
        // This method is kept for API compatibility
    }

    public refreshToken() {
        // Token is handled via cookie, no action needed
        // This method is kept for API compatibility
    }

    private getCookieValue(name: string): string | null {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
            return parts.pop()?.split(";").shift() || null;
        }
        return null;
    }

    // Test function to check API connectivity
    async testApiConnection() {
        try {
            const response = await fetch(
                `${window.location.origin}/admin/client`,
                {
                    method: "GET",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${
                            this.getCookieValue(SESSION_COOKIE_NAME) || ""
                        }`,
                        "X-CSRF-Token":
                            this.getCookieValue(CSRF_COOKIE_NAME) || ""
                    }
                }
            );

            const text = await response.text();

            if (response.ok) {
                return JSON.parse(text);
            } else {
                throw new Error(
                    `HTTP ${response.status}: ${text.substring(0, 100)}`
                );
            }
        } catch (error) {
            throw error;
        }
    }

    // Client Management
    async getClients() {
        return await this.client.getClients();
    }

    async createClient(clientData: ICreateClientDto) {
        return await this.client.createClient(clientData);
    }

    async updateClient(id: string, clientData: IUpdateClientDto) {
        return await this.client.updateClient(id, clientData);
    }

    async deleteClient(id: string) {
        return await this.client.deleteClient(id);
    }

    async uploadClientIcon(clientId: string, file: File) {
        return await this.client.uploadClientIcon(clientId, file);
    }

    async deleteClientIcon(clientId: string) {
        return await this.client.deleteClientIcon(clientId);
    }

    async getClientById(id: string) {
        return await this.client.getClientById(id);
    }

    async addApiKey(clientId: string, apiKeyData: IAddApiKeyDto) {
        return await this.client.addApiKey(clientId, apiKeyData);
    }

    async removeApiKey(clientId: string, apiKeyName: string) {
        return await this.client.removeApiKey(clientId, apiKeyName);
    }

    async createClientRole(clientId: string, roleData: ICreateRole) {
        return await this.client.createClientRole(clientId, roleData);
    }

    async getClientRoles(clientId: string) {
        return await this.client.getClientRoles(clientId);
    }

    async deleteClientRole(clientId: string, roleIdent: string) {
        return await this.client.deleteClientRole(clientId, roleIdent);
    }

    // Role Management
    async getRoles() {
        return await this.client.getRoles();
    }

    async createRole(roleData: ICreateRole) {
        return await this.client.createRole(roleData);
    }

    async getRole(ident: string) {
        return await this.client.getRole(ident);
    }

    async deleteRole(ident: string) {
        return await this.client.deleteRole(ident);
    }

    // User Management
    async getUsers(query: Partial<IAdminUserQuery> = {}) {
        return await this.client.getUsers(query);
    }

    async getUserDetails(tid: string) {
        return await this.client.getUserDetails(tid);
    }

    async createUser(userData: ICreateUserDto) {
        return await this.client.createUser(userData);
    }

    async updateUserProfile(tid: string, userData: IUpdateUserDto) {
        return await this.client.updateUserProfile(tid, userData);
    }

    async verifyUser(tid: string) {
        return await this.client.verifyUser(tid);
    }

    async lockUser(tid: string) {
        return await this.client.lockUser(tid);
    }

    async unlockUser(tid: string) {
        return await this.client.unlockUser(tid);
    }

    async addUserRole(tid: string, role: string) {
        return await this.client.addUserRole(tid, role);
    }

    async removeUserRole(tid: string, role: string) {
        return await this.client.removeUserRole(tid, role);
    }

    async deleteUser(tid: string) {
        return await this.client.deleteUser(tid);
    }

    async privilegeUser(tid: string) {
        return await this.client.privilegeUser(tid);
    }

    async degradeUser(tid: string) {
        return await this.client.degradeUser(tid);
    }

    async getUserAppUsers(tid: string) {
        return await this.client.getUserAppUsers(tid);
    }

    async getAppUser(tid: string, appUserId: string) {
        return await this.client.getAppUser(tid, appUserId);
    }

    async getClientAppUsers(
        clientId: string,
        params?: { limit?: number; offset?: number }
    ): Promise<{ total: number; data: PopulatedAppUser[] }> {
        return await this.client.getClientAppUsers(clientId, params);
    }

    async updateAppUser(
        tid: string,
        appUserId: string,
        data: {
            roles?: string[];
            scopes?: string[];
            meta?: Record<string, string>;
        }
    ) {
        return await this.client.updateAppUser(tid, appUserId, data);
    }

    async deleteAppUser(tid: string, appUserId: string) {
        return await this.client.deleteAppUser(tid, appUserId);
    }

    // Invitation Management
    async getInvitations(query: Partial<IAdminInvitationQuery> = {}) {
        return await this.client.getInvitations(query);
    }

    async createInvitation(invitationData: ICreateInvitationAdminDto) {
        return await this.client.createInvitation(invitationData);
    }

    async deleteInvitation(id: string) {
        return await this.client.deleteInvitation(id);
    }

    async createMessage(data: ICreateMessageAdminDto) {
        return await this.client.createMessage(data);
    }

    async getRoleMessages(
        query: {
            read?: boolean;
            limit?: number;
            offset?: number;
            recipientRole?: string;
        } = {}
    ) {
        return await this.client.getRoleMessages(query);
    }

    async markRoleMessageRead(id: string) {
        return await this.client.markRoleMessageRead(id);
    }

    async markAllRoleMessagesRead() {
        return await this.client.markAllRoleMessagesRead();
    }

    // Data Cleanup
    async performDataCleanup(dryRun: boolean = true) {
        return await this.client.performDataCleanup(dryRun);
    }

    // Health Check
    async getSystemHealth() {
        return await this.client.getSystemHealth();
    }

    // Email Test
    async testEmail(
        email: string,
        emailType: "verification" | "reset-password" | "invitation"
    ) {
        return await this.client.testEmail(email, emailType);
    }

    // Dashboard Stats
    async getDashboardStats() {
        return await this.client.getDashboardStats();
    }
}

export const adminApiClient = AdminApiClient.getInstance();
